import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Info, AlertTriangle, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, pesanGalat } from "../lib/api";
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import LoginMascots from "../components/login/LoginMascots";
import useLoginEntrance from "../components/login/useLoginEntrance";
import "./Login.css";

const KUNCI_INGAT = "epplehub_ingat_username";

export default function Login({ onAuthenticated } = {}) {
  const { login } = useAuth();
  const root = useRef(null);
  useLoginEntrance(root, ".epple-login__intro, .epple-login__form > *, .epple-login__help");
  const nav = useNavigate();
  const [identitas, setIdentitas] = useState(() => localStorage.getItem(KUNCI_INGAT) || "");
  const [password, setPassword] = useState("");
  const [lihatPw, setLihatPw] = useState(false);
  const [ingat, setIngat] = useState(() => Boolean(localStorage.getItem(KUNCI_INGAT)));
  const [loading, setLoading] = useState(false);
  const [galat, setGalat] = useState("");
  const [focusedField, setFocusedField] = useState("");
  const [showForgot, setShowForgot] = useState(false);
  const [branding, setBranding] = useState({
    app_name: "EppleHub Operational System", header_logo_url: "",
    login_subtitle_under_logo: "Sistem Informasi Karyawan",
    login_card_title: "Selamat datang kembali",
    login_card_subtitle: "Masuk untuk mengelola toko, karyawan, dan keuangan.",
    login_show_demo_accounts: false, login_demo_notice: "", login_footer_note: "",
    login_notice_banner: "", login_notice_type: "info", login_button_label: "Masuk",
  });

  useEffect(() => {
    let aktif = true;
    api.get("/config/branding").then(r => {
      if (aktif) setBranding(b => ({ ...b, ...r.data }));
    }).catch(() => {});
    return () => { aktif = false; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setGalat(""); setLoading(true);
    try {
      const u = await login(identitas.trim(), password);
      if (ingat) localStorage.setItem(KUNCI_INGAT, identitas.trim());
      else localStorage.removeItem(KUNCI_INGAT);
      if (onAuthenticated) onAuthenticated(u);
      else nav(u?.role?.startsWith("finance_") ? "/finance" : "/", { replace: true });
    } catch (err) {
      setGalat(pesanGalat(err, "Login gagal. Periksa username dan password kamu."));
      setLoading(false);
    }
  };
  const mascotVariant = loading ? "celebrate" : focusedField === "password" || (lihatPw && password) ? "shy" : focusedField === "username" ? "curious" : "idle";

  return (
    <>
      <main ref={root} className="epple-login" data-testid="login-page">
        <div className="epple-login__ambient" aria-hidden="true" />
        <div className="epple-login__layout">
          <section className="epple-login__card" aria-labelledby="login-heading">
            <header className="epple-login__brand">
              <LoginMascots variant={mascotVariant} className="epple-login__mascots" />
              {branding.header_logo_url && <img className="epple-login__logo" src={branding.header_logo_url} alt="" />}
              <h1 id="login-heading" data-testid="login-app-name">{branding.app_name}</h1>
              {branding.login_subtitle_under_logo && <p data-testid="login-subtitle-logo">{branding.login_subtitle_under_logo}</p>}
            </header>

            <div className="epple-login__intro">
              <h2 data-testid="login-card-title">{branding.login_card_title}</h2>
              <p data-testid="login-card-subtitle">{branding.login_card_subtitle}</p>
            </div>

            {branding.login_notice_banner && (
              <div className={`epple-login__notice epple-login__notice--${["warning", "success"].includes(branding.login_notice_type) ? branding.login_notice_type : "info"}`} data-testid="login-notice-banner">
                {branding.login_notice_type === "warning" ? <AlertTriangle size={16} />
                  : branding.login_notice_type === "success" ? <CheckCircle2 size={16} /> : <Info size={16} />}
                <span>{branding.login_notice_banner}</span>
              </div>
            )}

            <form onSubmit={submit} className="epple-login__form" data-testid="login-form" aria-busy={loading}>
              <div className="epple-login__field">
                <label htmlFor="login-username">Username</label>
                <input id="login-username" type="text" required value={identitas}
                  onChange={e => setIdentitas(e.target.value)} disabled={loading}
                  onFocus={() => setFocusedField("username")} onBlur={() => setFocusedField("")}
                  data-testid="login-username" autoComplete="username" autoCapitalize="none" spellCheck={false}
                  placeholder="Nama pengguna atau email" aria-describedby={galat ? "login-error" : undefined} />
              </div>
              <div className="epple-login__field">
                <label htmlFor="login-password">Password</label>
                <div className="epple-login__password">
                  <input id="login-password" type={lihatPw ? "text" : "password"} required value={password}
                    onChange={e => setPassword(e.target.value)} disabled={loading}
                    onFocus={() => setFocusedField("password")} onBlur={() => setFocusedField("")}
                    data-testid="login-password" autoComplete="current-password" placeholder="Masukkan password"
                    aria-describedby={galat ? "login-error" : undefined} />
                  <button type="button" onClick={() => setLihatPw(v => !v)} disabled={loading}
                    aria-label={lihatPw ? "Sembunyikan password" : "Lihat password"} aria-pressed={lihatPw}
                    data-testid="login-lihat-password" className="epple-login__eye">
                    {lihatPw ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div className="epple-login__options">
                <label className="epple-login__remember">
                  <input type="checkbox" checked={ingat} onChange={e => setIngat(e.target.checked)}
                    data-testid="login-ingat" disabled={loading} />
                  <span>Ingat saya</span>
                </label>
                <button type="button" onClick={() => setShowForgot(true)} disabled={loading}
                  data-testid="link-forgot-password" className="epple-login__text-button">Lupa password?</button>
              </div>

              {galat && <p id="login-error" role="alert" className="epple-login__error" data-testid="login-error"><AlertTriangle size={16} /><span>{galat}</span></p>}
              <button type="submit" disabled={loading} data-testid="login-submit" className="epple-login__submit">
                {loading && <Loader2 size={18} className="epple-login__spinner" />}
                <span>{loading ? "Sebentar, ya…" : branding.login_button_label || "Masuk"}</span>
                {!loading && <ArrowRight size={17} aria-hidden="true" />}
              </button>
            </form>

            <p className="epple-login__help">Belum punya akun? <span>Hubungi admin toko.</span></p>
            {branding.login_show_demo_accounts && (
              <div className="epple-login__demo" data-testid="login-demo-box">
                <strong>Akun demo</strong>
                <p>{branding.login_demo_notice || "Minta username dan password demo ke admin toko."}</p>
              </div>
            )}
          </section>
          {branding.login_footer_note && <p className="epple-login__footer" data-testid="login-footer-note">{branding.login_footer_note}</p>}
        </div>
      </main>
      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)}
        onBerhasil={(akun) => { setIdentitas(akun); setPassword(""); setGalat(""); }} />}
    </>
  );
}
